import { useMemo, useState } from 'react';
import { useIsSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { makeStyles } from 'tss-react/mui';
import clientAvenyModel from '../../../../../../Model/clientAvenyModel';
import { GravplatsQuery_node_TEDGAGrav as IGravplatsNode } from '../Queries/_QueryTypes/GravplatsQuery';
import { GravplatsQuery_node_TEDGAGrav__Associations_Vanvard_All as IVanvardItem } from '../Queries/_QueryTypes/GravplatsQuery';
import { TEDGAVanvardLastMarkeringEnum } from '../../../../../../globalQueryTypes';
import GravTitelKort from '../GravTitelKort';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { EDListItemButton } from '../../../../../../BaseComponents/List/Components/EDListItem';
import { EDTextEdit } from '../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDList from '../../../../../../BaseComponents/List/EDList';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import EDCheckbox from '../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import mergeSort from '../../../../../../Utils/mergeSort';
import {
  getAktuellaVanvard,
  getVanvardsmarkeringLabel,
} from '../Utils/vanvardUtils';
import BildViewer from '../BildViewer';

const GravModel = clientAvenyModel.GA.BusinessModels.GAGrav;

interface IVanvardProps {
  sidNr: number;
  nodeData: IGravplatsNode | null | undefined;
  fullPage: boolean;
}

interface IVanvardDetailsProps {
  item: IVanvardItem | null;
}

type DialogMode = 'edit' | 'create';

const useStyles = makeStyles()(theme => ({
  item: {
    '&:hover': {
      background:
        theme.customProperties?.Fade?.BlueGray?.hover ??
        theme.palette.action.hover,
    },
  },
  selectedItem: {
    background:
      theme.customProperties?.Fade?.BlueGray?.selected ??
      theme.palette.action.selected,
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(0.25),
    },
  },
  labelHistorik: {
    color: theme.palette.text.secondary,
  },
  valuesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5, 1.5),
    },
  },
  valueItem: {
    minWidth: 120,
    [theme.breakpoints.down('sm')]: {
      minWidth: 80,
    },
  },
  valueLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem',
    },
  },
  valueText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  secondaryActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  listItemContent: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    padding: theme.spacing(1, 0),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5, 0),
    },
  },
  paper: {
    maxWidth: '670px',
    background: theme.palette.background.default,
  },
}));

const VanvardDetails: React.FC<IVanvardDetailsProps> = ({ item }) => {
  if (!item) return null;

  return (
    <EDBox display="flex" flexDirection="column" gap={2}>
      {item?._Associations?.Bild?.BILD ? (
        <EDBox>
          <BildViewer
            blobId={item._Associations.Bild.BILD}
            alt="Vanvårdsfoto"
            dialogTitle="Vanvårdsfoto"
            style={{ height: '200px', objectFit: 'cover' }}
          />
        </EDBox>
      ) : (
        <EDTypography>Foto saknas</EDTypography>
      )}

      <EDBox>
        <EDTypography variant="subtitle2" color="textSecondary">
          Besiktningsdatum
        </EDTypography>
        <EDTypography>{item.BESIKTNINGSDATUM || '-'}</EDTypography>
      </EDBox>

      <EDBox>
        <EDTypography variant="subtitle2" color="textSecondary">
          Beslutsdatum
        </EDTypography>
        <EDTypography>{item.BESLUTSDATUM || '-'}</EDTypography>
      </EDBox>

      <EDBox>
        <EDTypography variant="subtitle2" color="textSecondary">
          Markering
        </EDTypography>
        <EDTypography>
          {getVanvardsmarkeringLabel(item.VANVARDSMARKERING)}
        </EDTypography>
      </EDBox>

      <EDBox>
        <EDTypography variant="subtitle2" color="textSecondary">
          Signatur
        </EDTypography>
        <EDTypography>{item.SIGNATUR || '-'}</EDTypography>
      </EDBox>

      <EDBox>
        <EDTypography variant="subtitle2" color="textSecondary">
          Notering
        </EDTypography>
        <EDTypography>{item.NOTERING || '-'}</EDTypography>
      </EDBox>
    </EDBox>
  );
};

export const Vanvard: React.FC<IVanvardProps> = ({
  sidNr,
  nodeData,
  fullPage,
}) => {
  const { classes, cx } = useStyles();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editVanvard, setEditVanvard] = useState<IVanvardItem | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>('edit');
  const isSmallScreen = useIsSmallScreen();
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editNotering, setEditNotering] = useState('');

  const vanvardItems = useMemo(
    () => nodeData?._Associations?.Vanvard_All ?? [],
    [nodeData]
  );

  // Hämta pågående
  const aktuellaVanvardItems = useMemo(
    () => getAktuellaVanvard(vanvardItems),
    [vanvardItems]
  );

  // Filtrerad och sorterad lista baserat på checkbox
  const filteredVanvardItems = useMemo(() => {
    const items = showHistory ? vanvardItems : aktuellaVanvardItems;
    return mergeSort(items, (a, b) => (b.LOPNR ?? 0) - (a.LOPNR ?? 0));
  }, [vanvardItems, aktuellaVanvardItems, showHistory]);

  // Kolla om ett item är historik
  const isHistorik = (item: IVanvardItem): boolean => {
    return !aktuellaVanvardItems.some(aktuell => aktuell.LOPNR === item.LOPNR);
  };

  // Uppdatera effectiveSelectedIndex att använda filtrerad lista
  const effectiveSelectedIndex = selectedIndex ?? 0;
  const effectiveSelectedVanvard =
    filteredVanvardItems[effectiveSelectedIndex] ?? null;

  const handleShowHistoryChange = (checked: boolean) => {
    setShowHistory(checked);
    setSelectedIndex(null);
  };

  const hasHistorik = vanvardItems.length > aktuellaVanvardItems.length;

  const getValues = (item: IVanvardItem) =>
    [
      { label: 'Besiktningsdatum', value: item?.BESIKTNINGSDATUM },
      {
        label: 'Markering',
        value: getVanvardsmarkeringLabel(item?.VANVARDSMARKERING),
      },
      { label: 'Signatur', value: item?.SIGNATUR },
      { label: 'Beslutsdatum', value: item?.BESLUTSDATUM },
    ].filter(v => v.value);

  const handleSelectVanvard = (index: number) => {
    setSelectedIndex(index);
  };

  const handleOpenEditDialog = (item: IVanvardItem) => {
    setDialogMode('edit');
    setEditVanvard(item);
    setEditNotering(item.NOTERING || '');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditVanvard(null);
  };

  const handleSaveEdit = () => {
    // TODO: Implementera sparfunktion
    handleCloseEditDialog();
  };

  const lastMarkering = useMemo(() => {
    if (vanvardItems.length === 0) return null;
    return vanvardItems.reduce((prev, current) =>
      (current.LOPNR ?? 0) > (prev.LOPNR ?? 0) ? current : prev
    ).LastMarkering;
  }, [vanvardItems]);

  const canCreateVanvard = useMemo(() => {
    if (vanvardItems.length === 0) return true;
    return lastMarkering === TEDGAVanvardLastMarkeringEnum.GODKAND;
  }, [vanvardItems.length, lastMarkering]);

  return (
    <ObjektVySida
      sidNr={sidNr}
      caption={'Vanvård'}
      collectionName={GravModel.CollectionName}
    >
      <ObjektVyKolumner>
        <ObjektVyKolumn fullPage={fullPage}>
          <GravTitelKort nodeData={nodeData} />

          <ObjektVyKort
            className="position-relative"
            backgroundColor={'white'}
            caption="Vanvårdsuppgifter"
          >
            <ObjektVyKort noBoxShadow>
              <EDBox
                display={'flex'}
                justifyContent={'space-between'}
                flexDirection={isSmallScreen ? 'column' : 'row'}
                gap={1}
              >
                {hasHistorik && (
                  <EDCheckbox
                    label="Visa historik"
                    checked={showHistory}
                    onCheck={handleShowHistoryChange}
                  />
                )}
                <EDBox display="flex" gap={1}>
                  {canCreateVanvard ? (
                    <EDButton size="small" outlined>
                      Markera för vanvård
                    </EDButton>
                  ) : (
                    <>
                      <EDButton
                        size="small"
                        outlined
                        disabled={
                          lastMarkering ===
                          TEDGAVanvardLastMarkeringEnum.VANVARD_AR_3
                        }
                        onClick={() => {
                          setDialogMode('create');
                          setEditVanvard(null);
                          setEditNotering('');
                          setEditDialogOpen(true);
                        }}
                      >
                        Lägg till
                      </EDButton>
                      <EDButton size="small" outlined>
                        Godkänn
                      </EDButton>
                    </>
                  )}
                </EDBox>
              </EDBox>
            </ObjektVyKort>
            {vanvardItems.length > 0 ? (
              <EDList noPadding>
                {filteredVanvardItems.map((item, index) => {
                  const values = getValues(item);
                  const isSelected = index === effectiveSelectedIndex;
                  return (
                    <EDListItemButton
                      key={`${item?.LOPNR}-${index}`}
                      className={cx(classes.item, {
                        [classes.selectedItem]: isSelected,
                      })}
                      divider={index < vanvardItems.length - 1}
                      disableVerticalPadding={false}
                      selected={isSelected}
                      onClick={() => handleSelectVanvard(index)}
                    >
                      <EDBox className={classes.listItemContent}>
                        <EDBox sx={{ flex: 1 }}>
                          <EDTypography
                            variant="subtitle2"
                            className={cx(classes.label, {
                              [classes.labelHistorik]: isHistorik(item),
                            })}
                          >
                            Besiktning {item?.BESIKTNINGSDATUM || '-'}
                          </EDTypography>

                          <div className={classes.valuesContainer}>
                            {values.map((v, idx) => (
                              <div key={idx} className={classes.valueItem}>
                                <EDTypography
                                  variant="caption"
                                  className={classes.valueLabel}
                                >
                                  {v.label}
                                </EDTypography>
                                <EDTypography
                                  variant="body2"
                                  className={classes.valueText}
                                >
                                  {v.value || '-'}
                                </EDTypography>
                              </div>
                            ))}
                          </div>
                          {(item?._Associations?.Bild?.BILD ||
                            item?.NOTERING) && (
                            <EDBox
                              display="flex"
                              gap={1}
                              alignItems="center"
                              mt={1}
                            >
                              {item?._Associations?.Bild?.BILD && (
                                <EDIcon iconType="image" size="medium" />
                              )}
                              {item?.NOTERING && (
                                <EDIcon iconType="event_note" size="medium" />
                              )}
                            </EDBox>
                          )}
                        </EDBox>

                        <div className={classes.secondaryActions}>
                          <EDIconButton
                            icon="edit"
                            iconSize="medium"
                            margin="none"
                            padding="dense"
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenEditDialog(item);
                            }}
                            tooltip="Redigera"
                          />
                          {isSmallScreen && (
                            <EDIconButton
                              icon="eye_outline"
                              iconSize="medium"
                              margin="none"
                              padding="dense"
                              onClick={e => {
                                e.stopPropagation();
                                handleSelectVanvard(index);
                                setDetailsDialogOpen(true);
                              }}
                              tooltip="Detaljer"
                            />
                          )}
                        </div>
                      </EDBox>
                    </EDListItemButton>
                  );
                })}
              </EDList>
            ) : (
              <EDTypography>Inga vanvårdsuppgifter registrerade</EDTypography>
            )}
          </ObjektVyKort>
        </ObjektVyKolumn>
        {!isSmallScreen && (
          <ObjektVyKolumn fullPage={fullPage}>
            {vanvardItems.length > 0 && effectiveSelectedVanvard ? (
              <ObjektVyKort
                className="position-relative"
                backgroundColor={'white'}
                caption="Detaljer"
              >
                <VanvardDetails item={effectiveSelectedVanvard} />
              </ObjektVyKort>
            ) : (
              <ObjektVyKort
                className="position-relative"
                backgroundColor={'white'}
                caption="Detaljer"
              >
                <EDTypography>Inga vanvårdsuppgifter registrerade</EDTypography>
              </ObjektVyKort>
            )}
          </ObjektVyKolumn>
        )}
      </ObjektVyKolumner>

      {/* Dialog för redigering */}
      <EDDialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        title={
          dialogMode === 'edit'
            ? 'Redigera vanvårdsuppgift'
            : 'Lägg till vanvårdsinstans'
        }
        subtitle={
          dialogMode === 'edit'
            ? `Besiktning ${editVanvard?.BESIKTNINGSDATUM || ''}`
            : undefined
        }
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.paper }}
        footerActions={[
          { label: 'Avbryt', onClick: handleCloseEditDialog },
          { label: 'Spara', onClick: handleSaveEdit },
        ]}
      >
        <EDBox display="flex" flexDirection="column" gap={2}>
          {dialogMode === 'edit' && (
            <>
              <EDBox>
                <EDTypography variant="subtitle2" color="textSecondary">
                  Vanvårdsmarkering
                </EDTypography>
                <EDTypography>
                  {getVanvardsmarkeringLabel(
                    editVanvard?.VANVARDSMARKERING ?? null
                  )}
                </EDTypography>
              </EDBox>

              <EDBox>
                <EDTypography variant="subtitle2" color="textSecondary">
                  Signatur
                </EDTypography>
                <EDTypography>{editVanvard?.SIGNATUR || '-'}</EDTypography>
              </EDBox>
            </>
          )}

          {/* Alltid synlig */}
          <EDTextEdit
            label="Notering"
            value={editNotering}
            onChange={value => setEditNotering(value)}
            multiline
            minRows={4}
            fullWidth
          />
          <EDBox>
            <EDButton>Lägg till bild</EDButton>
          </EDBox>
        </EDBox>
      </EDDialog>
      {/* Dialog för detaljer mobilskärm */}
      <EDDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        title="Detaljer"
        subtitle={`Besiktning ${
          effectiveSelectedVanvard?.BESIKTNINGSDATUM || ''
        }`}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.paper }}
        footerActions={[
          { label: 'Stäng', onClick: () => setDetailsDialogOpen(false) },
        ]}
      >
        <VanvardDetails item={effectiveSelectedVanvard} />
      </EDDialog>
    </ObjektVySida>
  );
};

export default Vanvard;
